import { useEffect, useRef } from "react";
import { connectSocket } from "../socket/socketClient";
import { subscribeToInvalidation } from "../helper/apiCache";

/**
 * Keeps a screen in sync with the data behind it.
 *
 * The callback runs whenever the given entity changes - whether the change was
 * made in this tab (add / edit / delete on this screen or another one) or by a
 * different user, in which case the server tells us over the websocket. By the
 * time the callback runs the client-side cache has already been dropped, so a
 * plain refetch always goes to the network and returns fresh data.
 *
 * @param {string} entity Entity name as it appears in the API path, e.g.
 *   "university", "organization", "student", "login". Pass "all" to react to
 *   every change.
 * @param {Function} onSync Refetch callback. Wrap it in useCallback, or rely on
 *   the ref below which always calls the latest version.
 *
 * @example
 *   useRealtimeSync("organization", useCallback(() => {
 *     dispatch(GetOrganizationAction({ page: 1, limit: 50 }));
 *   }, [dispatch]));
 */
export const useRealtimeSync = (entity, onSync) => {
  // Keeps the newest callback without re-subscribing on every render.
  const handlerRef = useRef(onSync);
  handlerRef.current = onSync;

  useEffect(() => {
    // Make sure a socket exists, otherwise remote changes never arrive.
    connectSocket();

    return subscribeToInvalidation(entity, (payload) => {
      if (typeof handlerRef.current === "function") handlerRef.current(payload);
    });
  }, [entity]);
};

export default useRealtimeSync;
